<template>
  <div class="bg-white p-4 rounded-lg mt-4 text-left text-textPrimary">
    <h2 class="text-md font-bold">Tipo de cálculo do pagamento</h2>

    <div class="flex flex-col gap-3 mt-4">
      <RadioButton
        id="Valor por hora"
        name="radio-group"
        value="Valor por hora"
        label="Valor por hora"
        v-model="paymentType"
      />
      <RadioButton
        id="Valor por plantão"
        name="radio-group"
        value="Valor por plantão"
        label="Valor por plantão"
        v-model="paymentType"
      />
    </div>

    <GenericInput :label="inputLabel" class="h-12 mt-4" v-model="inputValue" type="number" min="0">
      <template #prepend-inner>
        <span class="text-[#2F7F91] font-bold mr-1">R$</span>
      </template>
    </GenericInput>

    <div class="grid grid-cols-2 gap-4 mt-6">
      <div class="text-left">
        <h3 class="text-md font-bold">{{ paymentTypeLabel }}</h3>
        <p
          class="mt-1 text-sm flex items-center gap-1"
          :class="{ 'opacity-50': isOverrideLoading }"
        >
          <v-icon
            v-if="isOverrideLoading"
            :icon="mdiLoading"
            size="14"
            class="animate-spin text-primary"
          />
          <span>{{ hasOverrideError ? '—' : formatBRLOrDash(displayTotalValue) }}</span>
        </p>
      </div>
      <div class="text-left">
        <h3 class="text-md font-bold">Custo base</h3>
        <p
          class="mt-1 text-sm flex items-center gap-1"
          :class="{ 'opacity-50': isOverrideLoading }"
        >
          <v-icon
            v-if="isOverrideLoading"
            :icon="mdiLoading"
            size="14"
            class="animate-spin text-primary"
          />
          <span>{{ hasOverrideError ? '—' : formatBRLOrDash(displayBaseCost) }}</span>
        </p>
      </div>
      <div class="text-left">
        <h3 class="text-md font-bold">Custo excepcional</h3>
        <p
          class="mt-1 text-sm flex items-center gap-1"
          :class="{ 'opacity-50': isOverrideLoading }"
        >
          <v-icon
            v-if="isOverrideLoading"
            :icon="mdiLoading"
            size="14"
            class="animate-spin text-primary"
          />
          <span>{{ hasOverrideError ? '—' : formatBRLOrDash(displayExceptionalCost) }}</span>
        </p>
      </div>
      <div class="text-left">
        <h3 class="text-md font-bold">Prejuízo previsto</h3>
        <p
          class="mt-1 text-sm flex items-center gap-1"
          :class="[predictedResultColorClass, { 'opacity-50': isOverrideLoading }]"
        >
          <v-icon
            v-if="isOverrideLoading"
            :icon="mdiLoading"
            size="14"
            class="animate-spin text-primary"
          />
          <span>{{ hasOverrideError ? '—' : formatBRLOrDash(displayPredictedResult) }}</span>
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, type PropType } from 'vue'
import { mdiLoading } from '@mdi/js'
import GenericInput from '../Inputs/GenericInput.vue'
import RadioButton from '../RadioButton/index.vue'
import {
  calculateExceptionalCost,
  calculatePredictedResult,
  calculeteBaseCost,
  calculeteTotalAgreedValue
} from '@/utils/math'
import type { SimulateFinancialsResult } from '@/Network/Types/Requests/DoctorStationExceptionRequests'

// Local Intl formatter for currency to ensure off-mode parity
const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
const formatBRLOrDash = (v: string | number | null | undefined): string =>
  v === null || v === undefined ? '—' : brl.format(Number(v))

const props = defineProps({
  baseDuration: { type: Number, required: true, default: 0 },
  exceptionalDuration: { type: Number, required: true, default: 1 },
  baseValue: { type: Number, required: true, default: 0 },
  paymentModality: { type: String, required: true, default: 'no fluxo' },
  overrideFinancials: {
    type: Object as PropType<SimulateFinancialsResult | null>,
    required: false,
    default: undefined
  },
  isOverrideLoading: { type: Boolean, required: false, default: false },
  hasOverrideError: { type: Boolean, required: false, default: false }
})

const emit = defineEmits(['update:agreedValue'])

const VALOR_POR_HORA = 'Valor por hora'
const paymentType = ref(VALOR_POR_HORA)
const inputValue = ref(0)

const paymentTypeLabel = computed(() => {
  return paymentType.value === VALOR_POR_HORA ? 'Valor por plantão' : 'Valor por hora'
})

const inputLabel = computed(() => {
  return paymentType.value === VALOR_POR_HORA ? 'Valor/hora' : 'Valor total do plantão'
})

const tax = computed(() => {
  if (props.paymentModality === 'à vista') {
    const feeFromEnv = process.env.VUE_APP_EXCEPTIONAL_REQUEST_FEE_PAYMENT_ADVANCE
    const fee = Number(feeFromEnv)
    return isNaN(fee) ? 0.1 : fee
  }
  return 0
})

const agreedHourlyRate = computed(() => {
  if (paymentType.value === VALOR_POR_HORA) {
    return inputValue.value || 0
  }

  if (props.exceptionalDuration > 0) {
    const totalValue = inputValue.value || 0
    const duration = props.exceptionalDuration

    const rawRate = totalValue / duration

    const safeRate = Math.round(rawRate * 100) / 100

    return safeRate
  }

  return 0
})

const totalAgreedValue = computed(() => {
  return calculeteTotalAgreedValue(agreedHourlyRate.value, props.exceptionalDuration)
})

const calculatedDisplayValue = computed(() => {
  if (paymentType.value === 'Valor por plantão') {
    return agreedHourlyRate.value
  }
  return totalAgreedValue.value
})

const baseCost = computed(() => {
  return calculeteBaseCost(props.baseDuration, props.exceptionalDuration, props.baseValue)
})

const exceptionalCost = computed(() => {
  return calculateExceptionalCost(
    props.exceptionalDuration,
    props.baseDuration,
    agreedHourlyRate.value,
    tax.value
  )
})

const predictedResult = computed(() => {
  return calculatePredictedResult(exceptionalCost.value, baseCost.value, props.exceptionalDuration)
})

// --- Override (simulate) — só ativo quando o caller (wizard) fornece
// overrideFinancials !== undefined. undefined => comportamento 100% local,
// idêntico ao pré-existente (usado por NewExceptionalityRequest.vue).
const isOverrideActive = computed(() => props.overrideFinancials !== undefined)

const displayTotalValue = computed(() => {
  if (isOverrideActive.value) return props.overrideFinancials?.totalExceptionalValue ?? null
  return calculatedDisplayValue.value
})

const displayBaseCost = computed(() => {
  if (isOverrideActive.value) return props.overrideFinancials?.baseCost ?? null
  return baseCost.value
})

const displayExceptionalCost = computed(() => {
  if (isOverrideActive.value) return props.overrideFinancials?.exceptionalCost ?? null
  return exceptionalCost.value
})

const displayPredictedResult = computed(() => {
  if (isOverrideActive.value) return props.overrideFinancials?.estimatedLoss ?? null
  return predictedResult.value
})

const predictedResultColorClass = computed(() => {
  if (displayPredictedResult.value === null) return ''
  return Number(displayPredictedResult.value) >= 0 ? 'text-positive' : 'text-negative'
})

watch(
  [paymentType, inputValue, tax],
  () => {
    emit('update:agreedValue', {
      type: paymentType.value,
      value: inputValue.value,
      results: {
        total: totalAgreedValue.value,
        baseCost: baseCost.value,
        exceptionalCost: exceptionalCost.value,
        result: predictedResult.value
      }
    })
  },
  { immediate: true }
)
</script>

<style scoped>
.text-positive {
  color: #2f7f91;
}
.text-negative {
  color: #ef4444;
}
</style>
